import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../utilis/api";
import { toast } from "react-toastify";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const Checkout = () => {
  const navigate = useNavigate();
  const { user, token } = useSelector((state) => state.user);
  const { cartItems } = useSelector((state) => state.cart);

  // Shipping options
  const shippingOptions = {
    ground: 7.95,
    threeDay: 14.95,
    secondDay: 24.95,
    nextDay: 39.95,
  };
  const [shippingMethod, setShippingMethod] = useState("ground");

  // Main data object
  const [data, setData] = useState({
    orderItems: [],
    shippingAddress: {
      address: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      email: "",
      name: "",
      phone: "",
    },
    itemsPrice: 0,
    taxPrice: 0,
    shippingPrice: 0,
    shippingMethod: "ground",
    totalPrice: 0,
  });

  // Prefill user info if logged in
  useEffect(() => {
    if (user) {
      setData((prev) => ({
        ...prev,
        shippingAddress: {
          ...prev.shippingAddress,
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
        },
      }));
    }
  }, [user]);

  // Update order items
  useEffect(() => {
    setData((prev) => ({
      ...prev,
      orderItems: cartItems.map((item) => ({
        product: item._id, // matches schema field name
        quantity: item.quantity,
        price: item.variants[0].price,
      })),
    }));
  }, [cartItems]);

  // Calculate prices
  const shippingCost = shippingOptions[shippingMethod] || 0;
  const subtotalPrice = cartItems.reduce(
    (acc, item) => acc + item.variants?.[0].price * item.quantity,
    0,
  );
  const total = subtotalPrice + shippingCost;

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      shippingMethod,
      shippingPrice: shippingCost,
      itemsPrice: subtotalPrice,
      totalPrice: total,
    }));
  }, [shippingMethod, total]);

  // Handle input changes
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      shippingAddress: {
        ...prev.shippingAddress,
        [name]: value,
      },
    }));
  };

  // Checkout
  const handleCheckout = async () => {
    // Validation: all fields must be filled
    const { name, email, phone, address, city, state, postalCode, country } =
      data.shippingAddress;
    if (
      !name ||
      !email ||
      !phone ||
      !address ||
      !city ||
      !state ||
      !postalCode ||
      !country
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    // Build payload for both logged-in users and guests
    const payload = {
      ...data,
      paymentStatus: "Pending",
      userId: user?._id || null, // null for guest orders
      guestEmail: !user ? email : null, // only for guest orders
      guestName: !user ? name : null,   // only for guest orders
    };

    console.log(`🛒 Processing ${user ? 'User' : 'Guest'} Order:`, {
      customerType: user ? 'Registered User' : 'Guest Customer',
      email: user ? user.email : email,
      total: total.toFixed(2)
    });

    try {
      const response = await api.post("/orders/process", payload);

      if (response.data?.meta?.success) {
        const orderData = response.data?.data;
        const order = orderData?.order;
        
        console.log(`✅ Order Created Successfully:`, {
          orderId: order?.orderId || order?._id,
          customerType: order?.customerType,
          email: order?.customerEmail
        });
        
        toast.success(
          user 
            ? "Order placed successfully! Redirecting to payment..."
            : `Order placed successfully! Confirmation email sent to ${email}. Redirecting to payment...`,
          { autoClose: 4000 }
        );
        
        // Create Stripe checkout session
        const sessionRes = await api.post("/payments/create-checkout-session", {
          order,
        });

        if (sessionRes.data?.url) {
          // Redirect to Stripe
          window.location.href = sessionRes.data.url;
        } else {
          throw new Error("Payment session creation failed");
        }
      } else {
        toast.error(response.data?.meta?.message || "Order failed");
      }
    } catch (err) {
      console.error("Checkout Error:", err);
      const errorMessage = err?.response?.data?.meta?.message || 
                          err?.response?.data?.message || 
                          err.message || 
                          "Something went wrong. Please try again.";
      toast.error(errorMessage);
    }
  };

  // Fields definition
  const fields = [
    { label: "Email", name: "email", type: "email", col: 2 },
    { label: "Full Name", name: "name", type: "text", col: 2 },
    { label: "Street Address", name: "address", type: "text", col: 2 },
    { label: "Country", name: "country", type: "text", col: 1 },
    { label: "City", name: "city", type: "text", col: 1 },
    { label: "State", name: "state", type: "text", col: 1 },
    { label: "Zip Code", name: "postalCode", type: "text", col: 1 },
    { label: "Phone Number", name: "phone", type: "tel", col: 2 },
  ];

  return (
    <>
      <div className="bg-black">
        <Navbar />
      </div>

      {/* Breadcrumb */}
      <div className="bg-white border-b px-4 sm:px-10 lg:px-20 py-4 text-sm text-gray-500">
        <span className="text-gray-800 font-medium">
          <Link to="/">Home</Link>
        </span>
        <span className="mx-2 text-gray-400">/</span>
        Checkout
      </div>

      <div className="bg-gray-50 min-h-screen py-10 px-4 sm:px-6 md:px-10 lg:px-20">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
            Secure Checkout
          </h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base">
            Complete your order safely and quickly
          </p>
          {!user && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg inline-block">
              <p className="text-blue-700 text-sm">
                🛍️ <strong>Guest Checkout:</strong> No account required! You'll receive order confirmation via email.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-10 items-start">
          {/* LEFT SIDE */}
          <div className="w-full lg:w-2/3 space-y-6">
            {/* Shipping Address */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">
                Shipping Address
              </h2>

              <form className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {fields.map((field) => (
                  <div key={field.name} className={`md:col-span-${field.col}`}>
                    <label className="text-sm font-medium text-gray-600 mb-1 block">
                      {field.label}
                    </label>

                    <input
                      type={field.type}
                      name={field.name}
                      placeholder={field.label}
                      value={data.shippingAddress[field.name]}
                      onChange={handleAddressChange}
                      className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black transition bg-white"
                    />
                  </div>
                ))}
              </form>
            </div>

            {/* Shipping Method */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-5">
                Shipping Method
              </h2>

              <div className="space-y-3">
                {Object.entries(shippingOptions).map(([key, cost]) => (
                  <label
                    key={key}
                    className={`
                  flex items-center justify-between p-4 rounded-2xl border cursor-pointer
                  transition hover:border-black
                  ${
                    shippingMethod === key
                      ? "border-black bg-gray-50"
                      : "border-gray-200"
                  }
                `}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="shipping"
                        value={key}
                        checked={shippingMethod === key}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="w-4 h-4"
                      />

                      <span className="text-gray-700 text-sm sm:text-base">
                        {key === "ground" && "$7.95 Ground Shipping (5-7 Days)"}
                        {key === "threeDay" && "$14.95 3 Days Delivery"}
                        {key === "secondDay" && "$24.95 2nd Day Delivery"}
                        {key === "nextDay" && "$39.95 Next Day Delivery"}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="w-full lg:w-1/3">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:sticky lg:top-10">
              <h2 className="text-xl font-bold text-gray-800 mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 text-gray-700">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>${subtotalPrice.toFixed(2)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Delivery</span>
                  <span>${shippingCost.toFixed(2)}</span>
                </div>

                <div className="border-t pt-4 flex justify-between font-bold text-lg text-gray-900">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="
              w-full mt-6 py-4 rounded-2xl
              bg-black text-white font-semibold
              hover:bg-gray-800 transition
              active:scale-95 shadow-md
            "
              >
                Complete Order
              </button>

              <p className="text-xs text-gray-400 text-center mt-4">
                Secure encrypted payment • Fast processing
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Checkout;
